import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import DetailsCard from '../../components/DetailsCard/DetailsCard';
import CustomizableSnackbar from '../../components/Snackbar/Snackbar';
import { getMockInfo } from '../../api/mockAPI';
import AppBar from '../../components/AppBar/AppBar';
import Copyright from '../../components/Copyright/Copyright';
import EditDetailsCard from '../../components/DetailsCard/EditDetailsCard';
import { getUser, findMe, isLoggedIn, isPendingOrAccepted } from '../../api/usersAPI';
import { getHallName } from '../../api/hallsAPI';
import { searchFamilies, getFamilies, getFamilyByEmail, joinFamily, createFamily } from '../../api/familiesAPI';

import { LinearProgress } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  icon: {
    marginRight: theme.spacing(2),
  },
  heroContent: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(8, 0, 6),
  },
  heroButtons: {
    marginTop: theme.spacing(4),
  },
  cardGrid: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
  footer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(6),
  },
  searchBarContainer: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: "100%",
  },
  searchBar: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: "100%",
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  sectionMobile: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  grow: {
    flexGrow: 1,
  },
}));

export default function Directory({...props}) {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKey, setSearchKey] = useState('')
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarVariant, setSnackbarVariant] = useState('warning');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [user, setUser] = useState();
  const [mode, setMode] = useState();
  const [hasFamily, setHasFamily] = useState();
  const [favoriteIDs, setFavoriteIDs] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [search, setSearch] = useState({});
  const [me, setMe] = useState();
  const [hall, setHall] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const emptyRecord = {
    firstName: '',
    lastName: '',
    adults: [],
    dependents: [],
    emails: [],
    phones: [],
    address: '',
  }

  useEffect(() => {
    const initializeComponent = async () => {
      const loggedIn = isLoggedIn();
      if (!loggedIn) {
        props.history.push('/signin');
        return;
      }
      const pendingOrAccepted = await isPendingOrAccepted();
      const { isPending, isAccepted } = pendingOrAccepted;
      if (isPending) {
        props.history.push('/pending');
        return;
      }
      if (!isAccepted) {
        props.history.push('/rejected');
        return;
      }
      setIsLoading(true);
      const user = await getUser();
      setUser(user);
      const { favorites, hall: hallID, family, email } = user;
      setFavoriteIDs(favorites);
      const name = await getHallName(hallID);
      setHall(name);
      if (!family) {
        const foundFamily = await getFamilyByEmail(email);
        if (!foundFamily) {
          setSnackbarVariant('info');
          setSnackbarMessage('We could not find your family. Feel free to create an entry!');
          setIsSnackbarOpen(true);
        } else {
          try {
            joinFamily(foundFamily);
            setSnackbarVariant('info');
            setSnackbarMessage(`We found your family! We have automatically added you to the ${foundFamily.lastName} family.`);
            setIsSnackbarOpen(true);
            setHasFamily(true);
          } catch (e) {
            setSnackbarVariant('warning');
            setSnackbarMessage(`We found your family, but were unable to add you. We will try again later.`);
            setIsSnackbarOpen(true);
          }
        }
      } else {
        setHasFamily(true);
      }
      setIsLoading(false);
    }
    initializeComponent();
  }, [props.history]);

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const handleSearch = async () => {
    setIsLoading(true);
    if (searchTerm.length <= 2) {
      setSnackbarVariant('error');
      setSnackbarMessage('Please use more than two search characters.');
      setIsSnackbarOpen(true);
      return;
    }
    const newSearch = {...search};
    if (!search[searchTerm]) {
      const searchData = await searchFamilies(searchTerm);
      newSearch[searchTerm] = searchData;
    }
    setIsLoading(false);
    setSearch({...newSearch});
    setMode('search');
    setSearchTerm('');
    setSearchKey(searchTerm);
    handleSmoothScroll();
  }
  const handleFindFavorites = () => {
    setIsLoading(true);
    let favoritesData = [];
    if (favoriteIDs.length && !favorites.length) {
      favoritesData = getMockInfo(favoriteIDs.length, favoriteIDs);
      if (!favoritesData) {
        favoritesData = [];
      }
      if (!favoritesData.length) {
        favoritesData = [favoritesData];
      }
    }
    setIsLoading(false);
    setMode('favorites');
    setFavorites([...favorites, ...favoritesData]);
    setSearchTerm('');
    handleSmoothScroll();
  }
  const handleFindMe = async () => {
    setIsLoading(true);
    let myself;
    if (!me) {
      myself = await findMe();
    }
    setIsLoading(false);
    setMe(me || myself);
    setMode('me');
    setSearchTerm('');
    handleSmoothScroll();
  }
  const handleChange = (event) => {
    const { value } = event.target;
    switch (event.target.name) {
      case 'searchTerm':
        setSearchTerm(value);
        break;
      default:
        console.error('unexpected name', event.target.name);
    }
  }
  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      handleSearch();
    }
  }
  const makeCards = (data) => {
    return data.map((family) => {
      return <DetailsCard
        setIsSnackbarOpen={setIsSnackbarOpen}
        setSnackbarVariant={setSnackbarVariant}
        setSnackbarMessage={setSnackbarMessage}
        handleRecordUpdate={handleRecordUpdate}
        handleFavorite={handleFavorite}
        handleCancel={handleCancel}
        key={family.id}
        family={family}
        favorited={favoriteIDs.includes(family.id)}
        currentUser={user}
      />
    });
  }
  const handleRecordUpdate = (newRecord) => {
    if (me && me.id === newRecord.id) {
      setMe({...newRecord});
    }
    if (user.favorites.length && favorites) {
      for (let i = 0; i < favorites.length; i++) {
        const { id: old } = favorites[i];
        const { id: update } = newRecord;
        if (old === update) {
          favorites[i] = newRecord;
          setFavorites([...favorites]);
          break;
        }
      }
    }
    if (search) {
      for (let i = 0; i < search.length; i++) {
        const { id: old } = search[i];
        const { id: update } = newRecord;
        if (old === update) {
          search[i] = newRecord;
          setSearch([...search]);
          break;
        }
      }
    }
    setIsEditing(false);
    setMode('search');
  }
  const handleFavorite = (id) => {
    const i = favoriteIDs.indexOf(id);
    const j = favorites.map((x) => x.id).indexOf(id);
    i >= 0 ? 
      favoriteIDs.splice(i, 1) && favorites.splice(j, 1) : 
      favoriteIDs.push(id);
    setFavoriteIDs([...favoriteIDs]);
    let found = [];
    if (i < 0) {
      if (me && me.id === id) {
        found = [me]
      } else {
        for (const key in search) {
          for (let j = 0; j < search[key].length; j++) {
            if (search[key][j].id === id) {
              const alreadyFound = favorites.map((x) => x.id).indexOf(id);
              if (alreadyFound < 0) {
                found = [search[key][j]];
              }
              break;
            }
          }
        }
      }
    }
    setFavorites([...favorites, ...found ]);
  };
  const handleAdd = () => {
    if (isEditing) {
      return;
    }
    setIsEditing(true);
    setMode('edit');
  }
  const handlePrint = async () => {
    const newSearch = {...search}
    if (!newSearch['print']) {
      setIsLoading(true);
      const searchData = await getFamilies();
      newSearch['print'] = searchData;
    }
    setIsLoading(false);
    setSearchKey('print');
    setMode('search');
    setSearch({...newSearch})
  }
  const handleNewRecord = (family) => {
    delete family.id;
    try {
      createFamily(family);
      setSnackbarVariant('success');
      setSnackbarMessage('New family created!');
      setIsSnackbarOpen(true);
    } catch (e) {
      console.error(e.message);
      setSnackbarVariant('error');
      setSnackbarMessage('There was an error creating the family. Please try again later.');
      setIsSnackbarOpen(true);
    }
    setIsEditing(false);
    setMode('');
  }
  const handleCancel = () => {
    setIsEditing(false);
    setMode('');
  }
  const handleFindAll = async () => {
    await handlePrint();
    handleSmoothScroll();
  }
  const handleSmoothScroll = () => {
    document.getElementById('search-container').scrollIntoView({ 
      behavior: 'smooth' 
    });
  }
  return (
    <React.Fragment>
      <CustomizableSnackbar
        open={isSnackbarOpen}
        handleClose={handleSnackbarClose}
        variant={snackbarVariant}
        message={snackbarMessage}
      />
      <CssBaseline />
      <AppBar 
        hasFamily={hasFamily}
        user={user}
        setUser={setUser}
        history={props.history}
        handlePrint={handlePrint}
        handleAdd={handleAdd}
        isEditing={isEditing}
      />
      <main>
        {isLoading ? <LinearProgress color='secondary' /> : ''}
        {/* Hero unit */}
        <div className={classes.heroContent}>
          <Container maxWidth="sm">
            <Typography component="h1" variant="h2" align="center" color="textPrimary" gutterBottom>
            {hall} Directory
            </Typography>
            <Typography variant="h5" align="center" color="textSecondary" paragraph>
              Use the search field to search for a contact or use the buttons for a quick search!
            </Typography>
            <Grid container justify="center">
              <Grid item className={classes.searchBar}>
                <TextField
                  onSubmit={handleSearch}
                  id="search"
                  label="Search field"
                  type="search"
                  name="searchTerm"
                  margin="normal"
                  variant="outlined"
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  value={searchTerm}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small"
                          color="primary"
                          aria-label="search"
                          onClick={handleSearch}
                          disabled={searchTerm.length <= 2}
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <div className={classes.heroButtons}>
              <Grid container spacing={2} justify="center">
                <Grid item>
                  <Button 
                    disabled={mode === 'favorites'}
                    variant="outlined" 
                    color="primary"
                    onClick={handleFindFavorites}
                  >
                    Find Favorites
                  </Button>
                </Grid>
                <Grid item>
                  <Button 
                    disabled={mode === 'me'}
                    variant="outlined"
                    color="primary"
                    onClick={handleFindMe}
                  >
                    Find Me
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleFindAll}
                  >
                    Find All
                  </Button>
                </Grid>
              </Grid>
            </div>
          </Container>
        </div>
        <Container className={classes.cardGrid} id='search-container'>
          {/* End hero unit */}
          <Grid container spacing={4}>
            { mode === 'search' && search[searchKey] && makeCards(search[searchKey])}
            { mode === 'favorites' && makeCards(favorites)}
            { mode === 'me' && makeCards([me])}
            { mode === 'edit' && (
              <EditDetailsCard 
                handleCancel={handleCancel}
                handleRecordUpdate={handleNewRecord}
                family={emptyRecord}
                setIsSnackbarOpen={setIsSnackbarOpen}
                setSnackbarVariant={setSnackbarVariant}
                setSnackbarMessage={setSnackbarMessage}
              />
            )}
          </Grid>
        </Container>
      </main>
      {/* Footer */}
      <footer className={classes.footer}>
        <Typography variant="h6" align="center" gutterBottom>
          {hall} Directory
        </Typography>
        <Typography variant="subtitle1" align="center" color="textSecondary" component="p">
          Thanks for using the Gospel Hall Directory!
        </Typography>
        <Copyright />
      </footer>
      {/* End footer */}
    </React.Fragment>
  );
}
